import { FOCUS_RING_NO_RADIUS, Icon } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { Pillar } from '@/types/pillars';

import PillarIcon from '@/components/objectives/PillarIcon';
import PillarStatusMention from '@/components/objectives/PillarStatusMention';
import { derivePillarTileGradient } from '@/components/objectives/pillarSurface';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { getLocalizedText } from '@/utils/localization';

/** `FOCUS_RING`, drawn on the row's label while the visually hidden input inside it has focus. */
const LABEL_FOCUS_RING =
    'rounded-lg has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--color-primary-800)] has-[:focus-visible]:ring-offset-2';

/** The radio value of the "No pillar" row: an objective may be aligned with no pillar at all. */
export const NO_PILLAR_VALUE = 'no-pillar';

interface PillarChoiceRowProps {
    inputType: 'radio' | 'checkbox';
    name: string;
    pillar: Pillar;
    label: string;
    isSelected: boolean;
    isExpanded?: boolean;
    onChange: () => void;
    onToggleDescription?: () => void;
}

interface CheckMarkProps {
    isSelected: boolean;
    testIdSuffix: string;
}

function CheckMark({ isSelected, testIdSuffix }: CheckMarkProps) {
    return (
        <span
            className={`flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-[6px] border-2 ${
                isSelected ? 'border-primary bg-primary' : 'border-obj-line bg-white'
            }`}
            data-testid={`objective-form-pillar-check-${testIdSuffix}`}
            aria-hidden
        >
            {isSelected && (
                <Icon
                    name={'essential-checkmark'}
                    size={14}
                    colorClassName={'text-white'}
                    strokeWidth={3}
                />
            )}
        </span>
    );
}

/** One pillar choice: icon tile, name and check mark, plus an optional description toggle. */
export function PillarChoiceRow({
    inputType,
    name,
    pillar,
    label,
    isSelected,
    isExpanded = false,
    onChange,
    onToggleDescription,
}: PillarChoiceRowProps) {
    const { t } = useTranslation();
    const description = getLocalizedText(pillar.description);

    return (
        <div
            className={`relative w-full rounded-[8px] border transition-colors ${
                isSelected
                    ? // A literal hex, not `bg-primary/5`: the compiled ui-library CSS clobbers
                      // this app's opacity variants of shared colour tokens.
                      'border-primary bg-[#10CFBD]/5 shadow-sm'
                    : 'border-[rgba(238,251,255,1)] bg-white/90 hover:bg-white'
            }`}
        >
            <div className={'flex min-h-[66px] w-full items-center gap-2 px-4 py-3'}>
                {/* The label's `::after` stretches over the whole tile, so its padding selects
                    too; the description toggle stays a sibling button (never nested in the
                    label) and sits above that overlay through its own `relative`. */}
                <label
                    className={`${LABEL_FOCUS_RING} flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-3 after:absolute after:inset-0`}
                    data-testid={`objective-form-pillar-row-${pillar.id}`}
                >
                    <input
                        type={inputType}
                        className={'sr-only'}
                        name={name}
                        value={pillar.id}
                        checked={isSelected}
                        onChange={onChange}
                    />

                    <span className={'flex min-w-0 flex-1 items-center gap-3'}>
                        <span
                            className={
                                'flex h-[40px] w-[40px] flex-shrink-0 items-center justify-center rounded-[8px] shadow-sm'
                            }
                            style={{
                                background: derivePillarTileGradient(
                                    resolveCompanyValueIconBackground(
                                        pillar.icon,
                                        pillar.iconBackground,
                                    ),
                                ),
                            }}
                            aria-hidden
                        >
                            <PillarIcon
                                icon={pillar.icon}
                                size={22}
                            />
                        </span>

                        {/* A long pillar name wraps onto further lines inside the row instead of
                            running under the check mark: `min-w-0` lets the span shrink below its
                            single-line width, and `break-words` splits a word longer than the row. */}
                        <span className={'text-navy min-w-0 text-[16px] font-medium break-words'}>
                            {label}
                        </span>

                        <PillarStatusMention status={pillar.status} />
                    </span>

                    <CheckMark
                        isSelected={isSelected}
                        testIdSuffix={pillar.id}
                    />
                </label>

                {description && (
                    <button
                        type={'button'}
                        onClick={onToggleDescription}
                        aria-expanded={isExpanded}
                        aria-label={t('objectives.form.showDescription')}
                        className={`${FOCUS_RING_NO_RADIUS} relative flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-md hover:bg-black/5`}
                    >
                        <Icon
                            name={'arrow-arrow-down'}
                            size={16}
                            colorClassName={'text-obj-muted'}
                            rotate={isExpanded ? 180 : 0}
                        />
                    </button>
                )}
            </div>

            {description && isExpanded && (
                <div className={'px-4 pb-3 pl-[68px]'}>
                    <p className={'text-obj-muted text-[14px] leading-relaxed'}>{description}</p>
                </div>
            )}
        </div>
    );
}

interface NoPillarChoiceRowProps {
    name: string;
    label: string;
    isSelected: boolean;
    onChange: () => void;
}

/** The "No pillar" choice: a plain line below the pillars, offered without being put forward. */
export function NoPillarChoiceRow({ name, label, isSelected, onChange }: NoPillarChoiceRowProps) {
    return (
        <label
            className={`${LABEL_FOCUS_RING} flex cursor-pointer items-center gap-3 px-4 py-1 text-[14px] ${
                isSelected ? 'text-navy' : 'text-obj-muted hover:text-navy'
            }`}
            data-testid={'objective-form-pillar-row-none'}
        >
            <input
                type={'radio'}
                className={'sr-only'}
                name={name}
                value={NO_PILLAR_VALUE}
                checked={isSelected}
                onChange={onChange}
            />

            <CheckMark
                isSelected={isSelected}
                testIdSuffix={'none'}
            />

            {label}
        </label>
    );
}
